import { useState } from 'react';
import type { FormEvent } from 'react';
import { choosePassword, resetPassword, sendResetCode, signIn, signOut } from './auth';
import type { Session } from './types';

type Step = 'password' | 'choose' | 'forgot' | 'code';
const RULE = 'At least 12 characters, with an upper-case letter, a lower-case letter and a number.';
const strong = (p: string) => p.length >= 12 && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

/**
 * Sign-in for both CRMs: email and password, first sign-in after an invite (choose your own
 * password), and forgot password (a code by email). `refuse` turns a login away after it
 * signs in (the admin CRM, for anyone who isn't LeadsIRL); it is signed straight back out.
 */
export function SignIn({ title, subtitle, onSignedIn, refuse }: {
  title: string;
  subtitle: string;
  onSignedIn: (s: Session) => void;
  refuse?: (s: Session) => string | null;
}) {
  const [step, setStep] = useState<Step>('password');
  // Starts empty. Your own browser's password manager fills it in; a stranger's computer does not.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [code, setCode] = useState('');
  const [cognitoSession, setCognitoSession] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const go = (next: Step, message = '') => {
    setStep(next);
    setErr('');
    setNote(message);
    setPassword('');
    setAgain('');
    setCode('');
  };
  const done = (s: Session) => {
    const no = refuse?.(s);
    if (no) {
      signOut(s);
      go('password');
      setErr(no);
      return;
    }
    onSignedIn(s);
  };
  const run = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await fn();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const newPasswordOk = () => {
    if (!strong(password)) throw new Error(RULE);
    if (password !== again) throw new Error('The two passwords are not the same.');
  };

  // Logins are made with the email in small letters (sign-in also tries it as typed).
  const plainEmail = () => email.trim().toLowerCase();
  const onPassword = run(async () => {
    const r = await signIn(email.trim(), password);
    if ('session' in r) return done(r.session);
    setEmail(r.email);
    if ('choosePassword' in r) {
      setCognitoSession(r.cognitoSession);
      return go('choose', 'Welcome. Choose your own password to finish signing in.');
    }
    // LeadsIRL reset it, maybe days ago: that email's code may have run out, so send a fresh one.
    try {
      await sendResetCode(r.email);
      go('code', 'Your password was reset. We just emailed you a code: enter it with a new password.');
    } catch {
      go('code', 'Your password was reset. Enter the code from your email with a new password, or send a new code.');
    }
  });
  const onChoose = run(async () => {
    newPasswordOk();
    done(await choosePassword(email.trim(), password, cognitoSession));
  });
  const onForgot = run(async () => {
    if (!email.trim()) throw new Error('Type your email first.');
    await sendResetCode(plainEmail());
    go('code', `If ${plainEmail()} has a login, a code is on its way. Check your email.`);
  });
  const onCode = run(async () => {
    if (!/^\d{6}$/.test(code.trim())) throw new Error('The code is the 6 digits in the email.');
    newPasswordOk();
    await resetPassword(plainEmail(), code.trim(), password);
    go('password', 'Password changed. Sign in with it.');
  });

  const newPasswordFields = (
    <>
      <label>
        New password
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
      </label>
      <label>
        Type it again
        <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" />
      </label>
      <p className="hint">{RULE}</p>
    </>
  );

  return (
    <div className="login">
      {step === 'password' && (
        <form className="box" onSubmit={onPassword}>
          <h1>{title}</h1>
          <p className="sub">{subtitle}</p>
          {note && <p className="note">{note}</p>}
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </label>
          <label>
            Password
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </label>
          {err && <p className="err">{err}</p>}
          <button className="primary" disabled={busy} type="submit">{busy ? 'Signing in…' : 'Sign in'}</button>
          <button type="button" className="link" onClick={() => go('forgot')}>Forgot your password?</button>
        </form>
      )}
      {step === 'choose' && (
        <form className="box" onSubmit={onChoose}>
          <h1>Choose your password</h1>
          <p className="sub">{email}</p>
          {note && <p className="note">{note}</p>}
          {newPasswordFields}
          {err && <p className="err">{err}</p>}
          <button className="primary" disabled={busy} type="submit">{busy ? 'Saving…' : 'Save and sign in'}</button>
          <button type="button" className="link" onClick={() => go('password')}>Back to sign in</button>
        </form>
      )}
      {step === 'forgot' && (
        <form className="box" onSubmit={onForgot}>
          <h1>Forgot your password?</h1>
          <p className="sub">We'll email you a code to set a new one.</p>
          <label>
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </label>
          {err && <p className="err">{err}</p>}
          <button className="primary" disabled={busy} type="submit">{busy ? 'Sending…' : 'Email me a code'}</button>
          <button type="button" className="link" onClick={() => go('password')}>Back to sign in</button>
        </form>
      )}
      {step === 'code' && (
        <form className="box" onSubmit={onCode}>
          <h1>Set a new password</h1>
          <p className="sub">{email}</p>
          {note && <p className="note">{note}</p>}
          <label>
            Code from the email
            <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} />
          </label>
          {newPasswordFields}
          {err && <p className="err">{err}</p>}
          <button className="primary" disabled={busy} type="submit">{busy ? 'Saving…' : 'Save new password'}</button>
          <button type="button" className="link" disabled={busy} onClick={() => void onForgot({ preventDefault() {} } as FormEvent)}>Send a new code</button>
          <button type="button" className="link" onClick={() => go('password')}>Back to sign in</button>
        </form>
      )}
    </div>
  );
}
